import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../../../auth/state';
import SharedHeader from '../../../../../components/SharedHeader';
import DashboardFooter from '../../../../../components/DashboardFooter';
import { collection, doc, setDoc } from 'firebase/firestore';
import { db } from '../../../../../firebase/config';
import { 
  FaMoneyBill, 
  FaUserAlt,
  FaPhoneAlt,
  FaEnvelope,
  FaGlobe,
  FaUserTie,
  FaClock,
  FaChalkboardTeacher,
  FaDollarSign,
  FaStickyNote,
  FaExchangeAlt,
  FaTimes,
  FaCalendarAlt
} from 'react-icons/fa';
import './register_expense.css';

const RegisterExpense = () => {
  const navigate = useNavigate();
  const { isAdmin, user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [showConverter, setShowConverter] = useState(false);

  // # Currency configuration
  const currencies = [
    { code: 'USD', symbol: '$', label: 'US Dollar (USD)' },
    { code: 'EGP', symbol: 'E£', label: 'Egyptian Pound (EGP)' },
    { code: 'EUR', symbol: '€', label: 'Euro (EUR)' },
    { code: 'GBP', symbol: '£', label: 'British Pound (GBP)' },
    { code: 'SAR', symbol: 'SR', label: 'Saudi Riyal (SAR)' },
    { code: 'AED', symbol: 'د.إ', label: 'UAE Dirham (AED)' },
    { code: 'KWD', symbol: 'د.ك', label: 'Kuwaiti Dinar (KWD)' },
    { code: 'QAR', symbol: 'ر.ق', label: 'Qatari Riyal (QAR)' },
    { code: 'BHD', symbol: '.د.ب', label: 'Bahraini Dinar (BHD)' },
    { code: 'OMR', symbol: 'ر.ع.', label: 'Omani Rial (OMR)' }
  ];

  // # Expense categories
  const expenseCategories = [
    'software maintenance',
    'hosting',
    'subscription',
    'marketing',
    'content development',
    'investment',
    'budget leftover',
    'other'
  ];

  const [formData, setFormData] = useState({
    type: 'expense',  // # Document type
    category: '',     // # Expense category
    details: '',      // # Optional details
    amount: '',       // # Amount in selected currency
    currency: 'USD',  // # Default currency
    currency_symbol: '$',
    egp_total: ''    // # Amount in EGP
  });

  // # Handle currency change
  const handleCurrencyChange = (e) => {
    const selectedCurrency = currencies.find(curr => curr.code === e.target.value);
    setFormData(prev => ({
      ...prev,
      currency: selectedCurrency.code,
      currency_symbol: selectedCurrency.symbol
    }));
  };

  // # Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const timestamp = new Date();
      // # Generate ID using ISO timestamp for consistency
      const uniqueId = Math.random().toString(36).substring(2, 8);
      const expenseId = `expense_${timestamp.toISOString()}_${uniqueId}`;
      
      // # Get current year and month for document ID
      const docId = `${timestamp.getFullYear()}_${String(timestamp.getMonth() + 1).padStart(2, '0')}`;

      // # Get admin's full name from user object
      const adminName = user.firstName && user.lastName 
        ? `${user.firstName} ${user.lastName}`
        : user.displayName;

      // # Create expense data
      const expenseData = {
        metadata: {
          created_at: timestamp.toISOString(),
          updated_at: timestamp.toISOString()
        },
        register_admin: {
          uid: user.uid,
          email: user.email,
          name: adminName
        },
        transaction_type: 'expense',
        category: formData.category,
        details: formData.details,
        amount_native_currency: Number(formData.amount),
        currency: formData.currency,
        currency_symbol: formData.currency_symbol,
        egp_total: Number(formData.egp_total)
      };

      // # Add expense to Firestore in the financial collection
      await setDoc(doc(db, 'financial', docId), {
        expenses: {
          [expenseId]: expenseData
        }
      }, { merge: true });

      // # Navigate back to expenses page
      navigate('/admin/financial/expenses');
    } catch (error) {
      console.error('Error registering expense:', error);
      alert('Error registering expense. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // # Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'currency') {
      handleCurrencyChange(e);
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // # Redirect if not admin
  React.useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
    }
  }, [isAdmin, navigate]);

  return (
    <div className="expn_reg_page">
      <SharedHeader />
      
      <main className="expn_reg_content">
        <div className="expn_reg_header">
          <h1>Register Expense</h1>
          <button 
            className="expn_reg_back-button"
            onClick={() => navigate('/admin/financial/expenses')}
          >
            Back to Expenses
          </button>
        </div>

        <form className="expn_reg_form" onSubmit={handleSubmit}>
          {/* Expense Details Section */}
          <div className="expn_reg_section">
            <div className="expn_reg_section-header">
              <FaMoneyBill />
              <h2>Expense Details</h2>
            </div>
            
            <div className="expn_reg_form-group">
              <label>
                <FaUserAlt className="expn_reg_input-icon" />
                Category
              </label>
              <select 
                name="category" 
                value={formData.category}
                onChange={handleChange}
                required
              >
                <option value="">Select Category</option>
                {expenseCategories.map((category, index) => (
                  <option key={index} value={category}>
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div className="expn_reg_form-group">
              <label>
                <FaStickyNote className="expn_reg_input-icon" />
                Details (Optional)
              </label>
              <textarea
                name="details"
                value={formData.details}
                onChange={handleChange}
                rows="3"
                placeholder="Add any additional details here..."
              />
            </div>
          </div>

          {/* Amount Section */}
          <div className="expn_reg_section">
            <div className="expn_reg_section-header">
              <FaDollarSign />
              <h2>
                Amount
                <FaExchangeAlt 
                  className="expn_reg_converter-icon"
                  onClick={() => setShowConverter(true)}
                  title="Open Currency Converter"
                />
              </h2>
            </div>

            <div className="expn_reg_form-row">
              <div className="expn_reg_form-group">
                <label>
                  <FaDollarSign className="expn_reg_input-icon" />
                  Currency
                </label>
                <select 
                  name="currency" 
                  value={formData.currency}
                  onChange={handleChange}
                  required
                >
                  {currencies.map(curr => (
                    <option key={curr.code} value={curr.code}>
                      {curr.symbol} - {curr.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="expn_reg_form-group">
                <label>
                  <FaDollarSign className="expn_reg_input-icon" />
                  Amount
                </label>
                <div className="expn_reg_amount-wrapper">
                  <span className="expn_reg_currency-symbol">
                    {formData.currency_symbol}
                  </span>
                  <input 
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    required
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            <div className="expn_reg_form-group">
              <label>
                <FaDollarSign className="expn_reg_input-icon" />
                EGP Total
              </label>
              <div className="expn_reg_amount-wrapper">
                <span className="expn_reg_currency-symbol">E£</span>
                <input 
                  type="number"
                  name="egp_total"
                  value={formData.egp_total}
                  onChange={handleChange}
                  required
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button 
            type="submit" 
            className="expn_reg_submit-button"
            disabled={loading}
          >
            {loading ? 'Registering...' : 'Register Expense'}
          </button>
        </form>
      </main>

      {/* Currency Converter Popup */}
      {showConverter && (
        <div className="expn_reg_popup-overlay">
          <div className="expn_reg_popup-content">
            <button 
              className="expn_reg_popup-close"
              onClick={() => setShowConverter(false)}
            >
              <FaTimes />
            </button>
            <iframe
              src="https://www.xe.com/currencyconverter/"
              className="expn_reg_popup-iframe"
              title="Currency Converter"
            />
          </div>
        </div>
      )}

      <DashboardFooter />
    </div>
  );
};

export default RegisterExpense;
